import { DIAGRAM_TEAM, type DiagramElement, type DiagramTeam } from '@ukinfo/shared';
import { useId, type ReactNode } from 'react';
import { Button, Field, Input, Select, cn } from '../ui';
import { FACING_DIRECTIONS } from './diagramElements';
import { canReorder, type OrderDirection } from './diagramOrder';
import { ROW_MAX_COUNT, ROW_MAX_SPACING, ROW_MIN_COUNT, ROW_MIN_SPACING, isRowSource, type AlignMode, type RowDirection, type RowInput } from './diagramGroup';
import { TEAM_STYLE } from './PitchDiagram';
import { ToolIcon, type IconId } from './DiagramTools';

/**
 * Bereich "Auswahl" des Skizzeneditors, unter der Zeichenflaeche (so verschiebt sich das Feld nicht, wenn man ein Element
 * antippt). Mit Auswahl steht oben der Titel ("1 Spieler markiert", "5 Elemente markiert"), darunter in fester Reihenfolge die
 * Eigenschaften (bei einem Element seine eigenen, bei mehreren die gemeinsamen), "Anordnen" (Ausrichten, Drehen/Spiegeln),
 * "Bearbeiten" (Duplizieren, Reihe, Baustein, Loeschen) und "Auswahl aufheben". Ohne Auswahl zeigt der Bereich, was man
 * markieren kann, und die Befehle fuer die ganze Skizze.
 */

const TEAM_OPTIONS = DIAGRAM_TEAM.map((team) => ({ value: team, label: TEAM_STYLE[team].name }));

/** Namen der Elementarten fuer den Titel des Bereichs. */
export const ELEMENT_NAMES: Record<DiagramElement['type'], string> = {
  player: 'Spieler',
  cone: 'Hütchen',
  pole: 'Slalomstange',
  ring: 'Koordinationsring',
  hurdle: 'Hürde',
  goal: 'Tor',
  ball: 'Ball',
  arrow: 'Pfeil',
  measure: 'Maßlinie',
  zone: 'Zone',
  text: 'Text',
};

/** Elemente mit einer "Klein"-Stufe: nicht Tor, Zone, Pfeil und Masslinie (Text schon). */
export function canBeSmall(element: DiagramElement | undefined): boolean {
  return element !== undefined && element.type !== 'goal' && element.type !== 'zone' && element.type !== 'arrow' && element.type !== 'measure';
}

/** Bezug beim Zentrieren: die Feldmitte oder ein Bezugsobjekt. */
export type CenterMode = 'field' | 'object';

/**
 * Was beim Zentrieren am Feld wandert: die Auswahl als Gruppe ("group", Form und Abstaende bleiben) oder jedes
 * Element einzeln auf die Mittellinie ("each"). Bei einem Element und am Bezugsobjekt gibt es nur "each".
 */
export type CenterScope = 'group' | 'each';

const ALIGN_ACTIONS: ReadonlyArray<{ mode: AlignMode; short: string; label: string }> = [
  { mode: 'left', short: 'Links', label: 'Links ausrichten' },
  { mode: 'centerX', short: 'Mitte', label: 'Mitte ausrichten (waagrecht)' },
  { mode: 'right', short: 'Rechts', label: 'Rechts ausrichten' },
  { mode: 'top', short: 'Oben', label: 'Oben ausrichten' },
  { mode: 'centerY', short: 'Mitte', label: 'Mitte ausrichten (senkrecht)' },
  { mode: 'bottom', short: 'Unten', label: 'Unten ausrichten' },
];

/**
 * Knopf des Bereichs "Auswahl"; ein Umschalter ist hervorgehoben und meldet `aria-pressed`, ein aufklappbarer Bereich
 * `aria-expanded`. `danger` zeichnet ihn als Gefahrenknopf (Loeschen).
 */
export function ActionButton({
  label,
  pressed,
  expanded,
  controls,
  disabled,
  danger,
  icon,
  className,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  expanded?: boolean;
  controls?: string;
  disabled?: boolean;
  danger?: boolean;
  icon?: IconId;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  const on = pressed || expanded;
  return (
    <Button
      type="button"
      variant={on ? 'primary' : 'secondary'}
      size="sm"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-controls={controls}
      disabled={disabled}
      className={cn(danger && !on && 'border-rose-300 text-rose-700 hover:border-rose-400 hover:bg-rose-50', className)}
      onClick={onClick}
    >
      {icon && <ToolIcon id={icon} className="h-4 w-4" />}
      {children}
    </Button>
  );
}

/** Eine Gruppe im Bereich "Auswahl" mit kleiner Ueberschrift. */
export function PanelGroup({ title, testId, children }: { title: string; testId?: string; children: ReactNode }): JSX.Element {
  return (
    <div role="group" aria-label={title} data-testid={testId} className="space-y-2 border-t border-slate-200 pt-2">
      <p aria-hidden="true" className="text-[0.68rem] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </p>
      {children}
    </div>
  );
}

/** Titel des Bereichs: "1 Spieler markiert", bei mehreren Elementen "5 Elemente markiert". */
export function selectionTitle(picked: readonly DiagramElement[]): string {
  const first = picked[0];
  return picked.length === 1 && first ? `1 ${ELEMENT_NAMES[first.type]} markiert` : `${picked.length} Elemente markiert`;
}

export function SelectionPanel({
  elements,
  selection,
  disabled,
  rowOpen,
  rowPanelId,
  properties,
  overlapSlot,
  transformSlot,
  rowSlot,
  onOrder,
  onDuplicate,
  onRemove,
  onToggleSmall,
  onTeam,
  onFacing,
  onAlign,
  onDistribute,
  alignOpen,
  onToggleAlign,
  centerMode,
  onCenterMode,
  onCenter,
  referenceSet,
  onToggleRow,
  transformOpen,
  transformId,
  onToggleTransform,
  onSaveBlock,
  onClear,
}: {
  elements: readonly DiagramElement[];
  selection: readonly number[];
  disabled?: boolean;
  rowOpen: boolean;
  /** Kennung des Reihen-Bereichs, auf den der Knopf "Reihe" zeigt. */
  rowPanelId: string;
  /** Die Eigenschaften des einen markierten Elements (bei mehreren Elementen nicht gesetzt). */
  properties?: ReactNode;
  /** Die Liste "Hier liegen N" (`OverlapList`) unter dem Titel, wenn unter der angetippten Stelle mehrere Elemente liegen. */
  overlapSlot?: ReactNode;
  /** Der Bereich "Spiegeln und Drehen" und der Bereich "Reihe erzeugen": stehen unter ihren Knoepfen. */
  transformSlot?: ReactNode;
  rowSlot?: ReactNode;
  /** Auswahl ganz nach vorne oder hinten setzen (innerhalb ihrer Ebene; Pfeile bleiben, siehe `diagramOrder.ts`). */
  onOrder: (direction: OrderDirection) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onToggleSmall: () => void;
  onTeam: (team: DiagramTeam) => void;
  /** Blickrichtung (Grad) aller markierten Spieler setzen; `null` entfernt sie. */
  onFacing: (facing: number | null) => void;
  onAlign: (mode: AlignMode) => void;
  onDistribute: (axis: 'x' | 'y') => void;
  /** Das Menue "Ausrichten" ist offen; der Editor haelt den Zustand, weil das Antippen im Feld davon abhaengt. */
  alignOpen: boolean;
  onToggleAlign: () => void;
  centerMode: CenterMode;
  onCenterMode: (mode: CenterMode) => void;
  /** Waagrecht (`x`) oder senkrecht (`y`) zentrieren, am Feld (als Gruppe oder je Element) oder am Bezugsobjekt. */
  onCenter: (axis: 'x' | 'y', scope: CenterScope) => void;
  /** Im Modus "An Objekt" ist ein Bezugsobjekt gewaehlt (im Feld mit dickem Rahmen markiert). */
  referenceSet: boolean;
  onToggleRow: () => void;
  /** Die Gruppe "Spiegeln/Drehen" ist offen; sie steht unter den Knoepfen "Anordnen". */
  transformOpen: boolean;
  transformId: string;
  onToggleTransform: () => void;
  /** Die Auswahl als eigenen Baustein speichern (der Editor oeffnet den Dialog). */
  onSaveBlock: () => void;
  onClear: () => void;
}): JSX.Element {
  const alignId = useId();
  const groupCaptionId = useId();
  const lineCaptionId = useId();
  const picked = selection.map((index) => elements[index]).filter((element): element is DiagramElement => element !== undefined);
  const several = picked.length > 1;
  const smallable = picked.filter(canBeSmall);
  const allSmall = smallable.length > 0 && smallable.every((element) => 'size' in element && element.size === 'S');
  const allPlayers = picked.length > 0 && picked.every((element) => element.type === 'player');
  const teams = new Set(picked.map((element) => (element.type === 'player' ? element.team : null)));
  const commonTeam = teams.size === 1 ? [...teams][0] : null;
  const canRow = picked.length === 1 && isRowSource(picked[0]);
  // Blickrichtung gilt nur fuer die Spieler der Auswahl; zeigt die Auswahl gemeinsam eine, steht sie im Feld ("none" = alle ohne).
  const players = picked.filter((element): element is Extract<DiagramElement, { type: 'player' }> => element.type === 'player');
  const facings = new Set(players.map((player) => (player.facing === undefined ? 'none' : String(player.facing))));
  const commonFacing = facings.size === 1 ? [...facings][0]! : null;
  const selectClass = 'input min-h-11 w-full py-1.5 sm:min-h-0';

  return (
    <section aria-label="Auswahl" data-testid="selection-bar" className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/60 p-3">
      <h3 data-testid={several ? 'selection-count' : 'selection-title'} className="text-sm font-semibold text-slate-900">
        {selectionTitle(picked)}
      </h3>

      {overlapSlot}

      {several ? (
        <PanelGroup title="Eigenschaften" testId="diagram-properties">
          <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
            {allPlayers && (
              <Field label="Team">
                {(id) => (
                  <select id={id} aria-label="Team der Markierten" value={commonTeam ?? ''} disabled={disabled} onChange={(event) => onTeam(event.target.value as DiagramTeam)} className={selectClass}>
                    {commonTeam === null && (
                      <option value="" disabled>
                        Team ändern …
                      </option>
                    )}
                    {TEAM_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            {players.length > 0 && (
              <Field label="Blickrichtung">
                {(id) => (
                  <select
                    id={id}
                    aria-label="Blickrichtung der Markierten"
                    value={commonFacing ?? ''}
                    disabled={disabled}
                    onChange={(event) => onFacing(event.target.value === 'none' ? null : Number(event.target.value))}
                    className={selectClass}
                  >
                    {commonFacing === null && (
                      <option value="" disabled>
                        Blickrichtung …
                      </option>
                    )}
                    <option value="none">Keine Blickrichtung</option>
                    {FACING_DIRECTIONS.map((direction) => (
                      <option key={direction.angle} value={String(direction.angle)}>
                        {direction.glyph} Blick {direction.label}
                      </option>
                    ))}
                    {commonFacing !== null && commonFacing !== 'none' && !FACING_DIRECTIONS.some((direction) => String(direction.angle) === commonFacing) && (
                      <option value={commonFacing}>{commonFacing}°</option>
                    )}
                  </select>
                )}
              </Field>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <ActionButton label="Markierte klein" pressed={allSmall} disabled={disabled || smallable.length === 0} onClick={onToggleSmall}>
              Klein
            </ActionButton>
            <span className="text-xs text-slate-500">Verkleinert Hütchen, Spieler, Bälle und Text der Auswahl.</span>
          </div>
        </PanelGroup>
      ) : (
        properties
      )}

      <PanelGroup title="Anordnen" testId="selection-arrange">
        <div className="flex flex-wrap gap-1.5">
          <ActionButton label="Ausrichten" icon="align" expanded={alignOpen} controls={alignId} disabled={disabled} onClick={onToggleAlign}>
            Ausrichten
          </ActionButton>
          <ActionButton label="Spiegeln und Drehen" icon="rotate" expanded={transformOpen} controls={transformId} pressed={transformOpen} disabled={disabled} onClick={onToggleTransform}>
            Drehen / Spiegeln
          </ActionButton>
        </div>
        <div role="group" aria-label="Reihenfolge" className="flex flex-wrap items-center gap-1.5">
          <ActionButton label="Nach vorne" disabled={disabled || !canReorder(elements, selection, 'front')} onClick={() => onOrder('front')}>
            Nach vorne
          </ActionButton>
          <ActionButton label="Nach hinten" disabled={disabled || !canReorder(elements, selection, 'back')} onClick={() => onOrder('back')}>
            Nach hinten
          </ActionButton>
          <span className="text-xs text-slate-500">Gilt unter Elementen gleicher Art, z. B. ein Spieler über dem anderen. Pfeile behalten ihren Platz, er bestimmt die Schritte.</span>
        </div>
        {alignOpen && (
          <div id={alignId} role="group" aria-label="Ausrichten" className="space-y-1.5 rounded-lg bg-white p-1.5 ring-1 ring-slate-200">
            {several && (
              <>
                {/* Zwei Dreierreihen (links/Mitte/rechts, oben/Mitte/unten) und eine Zweierreihe: passt auch an schmalen Handys. */}
                <div role="group" aria-label="Bündig ausrichten" className="grid grid-cols-3 gap-1.5">
                  {ALIGN_ACTIONS.map((action) => (
                    <ActionButton key={action.mode} label={action.label} disabled={disabled} className="px-1" onClick={() => onAlign(action.mode)}>
                      {action.short}
                    </ActionButton>
                  ))}
                </div>
                <div role="group" aria-label="Gleichmäßig verteilen" className="grid grid-cols-2 gap-1.5">
                  <ActionButton label="Waagrecht verteilen" disabled={disabled || picked.length < 3} className="px-1" onClick={() => onDistribute('x')}>
                    Waagrecht verteilen
                  </ActionButton>
                  <ActionButton label="Senkrecht verteilen" disabled={disabled || picked.length < 3} className="px-1" onClick={() => onDistribute('y')}>
                    Senkrecht verteilen
                  </ActionButton>
                </div>
                {picked.length < 3 && <p className="text-xs text-slate-500">Verteilen braucht mindestens drei Elemente.</p>}
              </>
            )}
            {/* Zentrieren: erst der Bezug (Feld oder Objekt), dann die Richtung - vier Knoepfe in zwei Zweierreihen. */}
            <div role="group" aria-label="Zentrieren" className="space-y-1.5">
              <div role="group" aria-label="Bezug zum Zentrieren" className="grid grid-cols-2 gap-1.5">
                <ActionButton label="Am Feld zentrieren" pressed={centerMode === 'field'} disabled={disabled} className="px-1" onClick={() => onCenterMode('field')}>
                  Am Feld
                </ActionButton>
                <ActionButton label="An Objekt zentrieren" pressed={centerMode === 'object'} disabled={disabled} className="px-1" onClick={() => onCenterMode('object')}>
                  An Objekt
                </ActionButton>
              </div>
              {centerMode === 'field' && several ? (
                // Zwei Paare: die Gruppe als Ganzes in die Feldmitte, oder jedes Element einzeln auf die Mittellinie.
                <>
                  <div role="group" aria-labelledby={groupCaptionId} className="space-y-1">
                    <p id={groupCaptionId} className="text-xs font-medium text-slate-600">Gruppe zentrieren</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <ActionButton label="Gruppe waagrecht zentrieren" disabled={disabled} className="px-1" onClick={() => onCenter('x', 'group')}>
                        Waagrecht
                      </ActionButton>
                      <ActionButton label="Gruppe senkrecht zentrieren" disabled={disabled} className="px-1" onClick={() => onCenter('y', 'group')}>
                        Senkrecht
                      </ActionButton>
                    </div>
                  </div>
                  <div role="group" aria-labelledby={lineCaptionId} className="space-y-1">
                    <p id={lineCaptionId} className="text-xs font-medium text-slate-600">Auf Mittellinie</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <ActionButton label="Waagrecht auf Mittellinie" disabled={disabled} className="px-1" onClick={() => onCenter('x', 'each')}>
                        Waagrecht
                      </ActionButton>
                      <ActionButton label="Senkrecht auf Mittellinie" disabled={disabled} className="px-1" onClick={() => onCenter('y', 'each')}>
                        Senkrecht
                      </ActionButton>
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-1.5">
                  <ActionButton label="Waagrecht zentrieren" disabled={disabled} className="px-1" onClick={() => onCenter('x', 'each')}>
                    Waagrecht zentrieren
                  </ActionButton>
                  <ActionButton label="Senkrecht zentrieren" disabled={disabled} className="px-1" onClick={() => onCenter('y', 'each')}>
                    Senkrecht zentrieren
                  </ActionButton>
                </div>
              )}
              <p data-testid="center-hint" className="text-xs text-slate-500">
                {centerMode === 'field'
                  ? several
                    ? 'Gruppe: die Auswahl rückt als Ganzes in die Feldmitte, Form und Abstände bleiben. Auf Mittellinie: jedes Element einzeln auf die Mittellinie des Felds – danach stehen sie in einer Linie.'
                    : 'Das Element wird auf die Feldmitte gerückt.'
                  : referenceSet
                    ? several
                      ? 'Bezugsobjekt ist das dick umrandete Element und bleibt stehen; ein markiertes Element antippen wechselt es.'
                      : 'Bezugsobjekt ist das dick umrandete Element; ein anderes antippen wechselt es.'
                    : 'Tippe das Element an, an dessen Mitte ausgerichtet wird.'}
              </p>
            </div>
          </div>
        )}
        {transformSlot}
      </PanelGroup>

      <PanelGroup title="Bearbeiten" testId="selection-edit">
        <div className="flex flex-wrap gap-1.5">
          <ActionButton label="Auswahl duplizieren" icon="duplicate" disabled={disabled} onClick={onDuplicate}>
            Duplizieren
          </ActionButton>
          {canRow && (
            <ActionButton label="Reihe erzeugen" icon="row" expanded={rowOpen} controls={rowPanelId} pressed={rowOpen} disabled={disabled} onClick={onToggleRow}>
              Reihe erzeugen
            </ActionButton>
          )}
          <ActionButton label="Als Baustein speichern" icon="block" disabled={disabled} onClick={onSaveBlock}>
            Baustein speichern
          </ActionButton>
          <ActionButton label={several ? 'Markierte Elemente löschen' : 'Markiertes Element löschen'} icon="trash" danger disabled={disabled} onClick={onRemove}>
            Löschen
          </ActionButton>
        </div>
        {rowSlot}
      </PanelGroup>

      <div className="border-t border-slate-200 pt-2">
        <ActionButton label="Auswahl aufheben" icon="clear" onClick={onClear}>
          Auswahl aufheben
        </ActionButton>
      </div>
    </section>
  );
}

/**
 * Bereich "Auswahl" ohne Auswahl: erklaert, wie man markiert, und bietet die Befehle fuer die ganze Skizze - alles markieren,
 * spiegeln/drehen, alles loeschen, die Skizze entfernen (die beiden letzten als Gefahrenknoepfe).
 */
export function EmptySelection({
  disabled,
  empty,
  transformOpen,
  transformId,
  transformSlot,
  onSelectAll,
  onToggleTransform,
  onClearAll,
  onRemoveDiagram,
}: {
  disabled?: boolean;
  /** Die Skizze hat noch kein Element. */
  empty: boolean;
  transformOpen: boolean;
  transformId: string;
  transformSlot?: ReactNode;
  onSelectAll: () => void;
  onToggleTransform: () => void;
  onClearAll: () => void;
  onRemoveDiagram: () => void;
}): JSX.Element {
  return (
    <section aria-label="Auswahl" data-testid="diagram-selection-empty" className="space-y-3 rounded-xl border border-dashed border-slate-300 bg-white p-3">
      <div>
        <h3 data-testid="diagram-properties" className="text-sm font-semibold text-slate-900">
          Kein Element markiert
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">Im Modus „Auswählen“ ein Element antippen oder einen Rahmen aufziehen, um es zu bearbeiten.</p>
      </div>
      <PanelGroup title="Ganze Skizze">
        <div className="flex flex-wrap gap-1.5">
          <ActionButton label="Alles markieren" icon="all" disabled={disabled || empty} onClick={onSelectAll}>
            Alles markieren
          </ActionButton>
          <ActionButton label="Spiegeln und Drehen" icon="rotate" expanded={transformOpen} controls={transformId} pressed={transformOpen} disabled={disabled} onClick={onToggleTransform}>
            Drehen / Spiegeln
          </ActionButton>
          <ActionButton label="Alles löschen" icon="trash" danger disabled={disabled || empty} onClick={onClearAll}>
            Alles löschen
          </ActionButton>
          <ActionButton label="Skizze entfernen" icon="clear" danger disabled={disabled} onClick={onRemoveDiagram}>
            Skizze entfernen
          </ActionButton>
        </div>
        {transformSlot}
      </PanelGroup>
    </section>
  );
}

const DIRECTIONS: ReadonlyArray<{ value: RowDirection; label: string }> = [
  { value: 'horizontal', label: 'Waagrecht' },
  { value: 'vertical', label: 'Senkrecht' },
  { value: 'diagonal', label: 'Diagonal' },
  { value: 'target', label: 'Bis zu einem Punkt' },
];

/**
 * Bereich "Reihe erzeugen": Anzahl, Richtung und Abstand, mit Vorschau im Feld. Bei "bis zu einem
 * Punkt" tippt man den zweiten Punkt aufs Feld. Die Rechnung steckt in `planRow`; hier steht nur die Bedienung.
 */
export function RowPanel({
  id,
  input,
  problem,
  count,
  disabled,
  onChange,
  onApply,
  onCancel,
}: {
  id?: string;
  input: RowInput;
  /** Warum die Reihe gerade nicht moeglich ist - leer, wenn sie sich uebernehmen laesst. */
  problem: string | null;
  /** Anzahl neuer Elemente der Vorschau. */
  count: number;
  disabled?: boolean;
  onChange: (patch: Partial<RowInput>) => void;
  onApply: () => void;
  onCancel: () => void;
}): JSX.Element {
  const target = input.direction === 'target';
  return (
    <section id={id} aria-label="Reihe erzeugen" data-testid="row-panel" className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">Reihe erzeugen</h4>
        <p className="text-xs text-slate-500">
          Anzahl {ROW_MIN_COUNT} bis {ROW_MAX_COUNT} (mit dem markierten Element), Abstand {ROW_MIN_SPACING} bis {ROW_MAX_SPACING} Feldeinheiten.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="col-span-2 sm:col-span-1">
          <Field label="Richtung">
            {(fieldId) => (
              <Select id={fieldId} value={input.direction} disabled={disabled} onChange={(event) => onChange({ direction: event.target.value as RowDirection })}>
                {DIRECTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field label="Anzahl">
          {(fieldId) => (
            <Input
              id={fieldId}
              type="number"
              inputMode="numeric"
              min={ROW_MIN_COUNT}
              max={ROW_MAX_COUNT}
              value={input.count}
              disabled={disabled}
              // Enter darf das umgebende Formular nicht abschicken.
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              onChange={(event) => onChange({ count: event.target.value })}
            />
          )}
        </Field>
        <Field label="Abstand">
          {(fieldId) => (
            <Input
              id={fieldId}
              type="number"
              inputMode="numeric"
              min={ROW_MIN_SPACING}
              max={ROW_MAX_SPACING}
              value={input.spacing}
              disabled={disabled || target}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              onChange={(event) => onChange({ spacing: event.target.value })}
            />
          )}
        </Field>
      </div>
      <p role="status" data-testid="row-status" className={cn('text-xs', problem ? 'font-medium text-amber-700' : 'text-slate-600')}>
        {problem ?? `Vorschau: ${count} neue${count === 1 ? 's Element' : ' Elemente'} – im Feld blass zu sehen.`}
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="button" size="sm" disabled={disabled || problem !== null} onClick={onApply}>
          Übernehmen
        </Button>
      </div>
    </section>
  );
}
